import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { VILLA_PHOTOS } from "../data";

const ITEMS = [
    { src: VILLA_PHOTOS.living, label: "Gather in the living room", span: "md:col-span-7 md:row-span-2", h: "h-72 md:h-full" },
    { src: VILLA_PHOTOS.primary, label: "Unwind in the primary suite", span: "md:col-span-5", h: "h-64 md:h-64" },
    { src: VILLA_PHOTOS.kitchen, label: "Cook in the full kitchen", span: "md:col-span-5", h: "h-64 md:h-64" },
    { src: VILLA_PHOTOS.guest, label: "Settle into the guest bedroom", span: "md:col-span-4", h: "h-64" },
    { src: VILLA_PHOTOS.dining, label: "Dine together", span: "md:col-span-4", h: "h-64" },
    { src: VILLA_PHOTOS.bathroom, label: "Refresh & reset", span: "md:col-span-4", h: "h-64" },
    { src: VILLA_PHOTOS.balcony, label: "Morning coffee on the private balcony", span: "md:col-span-5", h: "h-64" },
    { src: VILLA_PHOTOS.pool, label: "Afternoons at the community pool", span: "md:col-span-4", h: "h-64" },
    { src: VILLA_PHOTOS.tennis, label: "Rally on the courts", span: "md:col-span-3", h: "h-64" },
];

export default function Gallery() {
    const [index, setIndex] = useState(null);

    return (
        <section id="gallery" className="py-24 lg:py-36 bg-[#0F2837] relative grain" data-testid="gallery-section">
            <div className="max-w-7xl mx-auto px-5 lg:px-8">
                <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="flex flex-wrap items-end justify-between gap-6 mb-12">
                    <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-[#C49A45] font-semibold">Villa photography</p>
                        <h2 className="font-serif-d font-light text-4xl sm:text-5xl lg:text-6xl text-white mt-4 leading-[1.05]">
                            Inside the villa.<br /><em className="text-[#E9D9AE]">Beyond the villa.</em>
                        </h2>
                    </div>
                    <p className="text-white/60 text-sm max-w-xs">Every frame from the real {`#6322`} listing — the spaces exactly as you'll find them.</p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-5" data-testid="bento-gallery-container">
                    {ITEMS.map((item, i) => (
                        <motion.button key={item.label} onClick={() => setIndex(i)}
                            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.7, delay: (i % 3) * 0.08 }}
                            className={`bento-item rounded-3xl text-left ${item.span}`} data-testid={`gallery-image-lightbox-trigger-${i}`}>
                            <img src={item.src} alt={item.label} className={`w-full ${item.h} object-cover`} loading="lazy" />
                            <span className="bento-veil" />
                            <span className="absolute bottom-4 left-5 right-5 text-white text-sm font-medium tracking-wide">{item.label}</span>
                        </motion.button>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {index !== null && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[95] bg-[#0C131D]/95 backdrop-blur flex items-center justify-center p-4"
                        onClick={() => setIndex(null)} data-testid="gallery-lightbox">
                        <button className="absolute top-5 right-5 text-white/80 hover:text-white p-2" onClick={() => setIndex(null)} data-testid="lightbox-close" aria-label="Close"><X size={28} /></button>
                        <button className="absolute left-3 sm:left-8 text-white/80 hover:text-white p-2" onClick={(e) => { e.stopPropagation(); setIndex((index - 1 + ITEMS.length) % ITEMS.length); }} data-testid="lightbox-prev" aria-label="Previous"><ChevronLeft size={36} /></button>
                        <motion.figure key={index} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
                            className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
                            <img src={ITEMS[index].src} alt={ITEMS[index].label} className="w-full max-h-[78vh] object-contain rounded-2xl" />
                            <figcaption className="mt-4 text-center text-white/80 font-serif-d text-xl">{ITEMS[index].label}</figcaption>
                        </motion.figure>
                        <button className="absolute right-3 sm:right-8 text-white/80 hover:text-white p-2" onClick={(e) => { e.stopPropagation(); setIndex((index + 1) % ITEMS.length); }} data-testid="lightbox-next" aria-label="Next"><ChevronRight size={36} /></button>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
